import { EFinancingProgram, EProvince, getEProvinceList } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledNumberField,
  ControlledSelect,
  ControlledSwitch,
  FieldPrerequisiteSpec,
  FieldReadyGate,
  FieldReadyGateProvider,
  renderSelectMenuItems,
  useReconcileFieldToOptions,
  useTranslatedOptionEnum,
} from '@if/mui'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, TextField, Typography } from '@mui/material'
import { MerchantAutocomplete } from '@src/components'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { Constants } from '@src/data/types/constants'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { useEffect, useMemo, type JSX } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ErsScoreAutocomplete from './components/ErsScoreAutocomplete'

// Mirror the assumptions baked into CalculatorSimulatePrequalificationService.BuildSyntheticApplicant
// on the backend. Surfaced here so callers can see the synthetic shape the prequal runs against.
const SYNTHETIC_PARAMS = {
  applicantAgeYears: 30,
  creditFileAgeYears: 5,
  defaultAnnualIncome: 250_000,
} as const

export interface SimulatePrequalFormValues {
  financingProgramId: EFinancingProgram | ''
  provinceIso: EProvince | ''
  ersScore: number | null
  hasCoApplicant: boolean
  merchantId: string | null
  requestedLoanAmount: number | null
  applicantAnnualIncome: number | null
  coApplicantAnnualIncome: number | null
}

export const SIMULATE_PREQUAL_DEFAULT_VALUES: SimulatePrequalFormValues = {
  financingProgramId: '',
  provinceIso: '',
  ersScore: null,
  hasCoApplicant: false,
  merchantId: null,
  requestedLoanAmount: null,
  applicantAnnualIncome: null,
  coApplicantAnnualIncome: null,
}

const MERCHANT_PREREQUISITES = [
  { name: 'financingProgramId', prefixKey: 'calculator', labelKey: 'financingProgram' },
] as const satisfies readonly FieldPrerequisiteSpec<SimulatePrequalFormValues, 'calculator'>[]

export const simulatePrequalSchema = yup.object({
  financingProgramId: yup.string().required(),
  provinceIso: yup.string().required(),
  ersScore: yup.number().required().positive(),
  merchantId: yup.string().required(),
  requestedLoanAmount: yup.number().required().positive(),
})

interface SimulatePrequalFormProps {
  methods: UseFormReturn<SimulatePrequalFormValues>
}

const SimulatePrequalForm = ({ methods }: SimulatePrequalFormProps): JSX.Element => {
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags()
  const { selectedCompany } = usePreferences()
  const { control, watch, setValue } = methods
  const financingProgramId = watch('financingProgramId')
  const hasCoApplicant = watch('hasCoApplicant')

  const canHaveCoApplicant =
    (financingProgramId && Constants.financingProgramConfigs[financingProgramId]?.canHaveCoapplicant) ?? false

  useEffect(() => {
    if (!canHaveCoApplicant && hasCoApplicant) {
      setValue('hasCoApplicant', false)
      setValue('coApplicantAnnualIncome', null)
    }
  }, [canHaveCoApplicant, hasCoApplicant, setValue])

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')

  const availableFinancingPrograms = useMemo(() => {
    let programs = getProgramsByCompanyId([selectedCompany!])
    if (!featureFlags.useIFXFinancingProgram) {
      programs = programs.filter((x) => x !== EFinancingProgram.IfXpress)
    }
    programs = programs.filter((x) => x !== EFinancingProgram.AutoRsla)
    if (!featureFlags.enableAutoPlus) {
      programs = programs.filter((x) => x !== EFinancingProgram.Auto)
    }
    return programs
  }, [selectedCompany, featureFlags])

  const availableFinancingProgramMenuItems = useMemo(
    () =>
      renderSelectMenuItems(availableFinancingPrograms, (item) => ({
        value: item,
        label: t(`EFinancingProgram.${item}`, { ns: 'enum' }),
      })),
    [availableFinancingPrograms, t],
  )

  const { isValid: isFinancingProgramValid } = useReconcileFieldToOptions({
    control,
    name: 'financingProgramId',
    options: availableFinancingPrograms,
    getKey: (p) => p,
  })

  return (
    <FieldReadyGateProvider>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          {isFinancingProgramValid ? (
            <ControlledSelect
              name="financingProgramId"
              control={control}
              label={t('calculator.financingProgram')}
              options={availableFinancingProgramMenuItems}
              onChange={() => setValue('merchantId', null)}
              disableEmptyOption
              fullWidth
            />
          ) : (
            <ControlledSelect
              name="financingProgramId"
              control={control}
              label={t('calculator.financingProgram')}
              options={[]}
              disabled
              disableEmptyOption
              fullWidth
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <ControlledSelect
            name="provinceIso"
            control={control}
            label={t('calculator.province')}
            options={provinceOptions}
            disableEmptyOption
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <ErsScoreAutocomplete name="ersScore" control={control} label={t('calculator.ersTier')} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FieldReadyGate
            control={control}
            requiredFields={MERCHANT_PREREQUISITES}
            selfName="merchantId"
            shell={
              <TextField
                label={t('calculator.merchant')}
                placeholder={t('calculator.merchantPlaceholder')}
                disabled
                fullWidth
              />
            }
          >
            {({ financingProgramId }) => (
              <MerchantAutocomplete
                control={control}
                name="merchantId"
                financingProgramId={financingProgramId as EFinancingProgram}
                label={t('calculator.merchant')}
              />
            )}
          </FieldReadyGate>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ControlledNumberField
            name="requestedLoanAmount"
            control={control}
            label={t('calculator.requestedLoanAmount')}
            isMoney
            disableDecimals
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ControlledNumberField
            name="applicantAnnualIncome"
            control={control}
            label={t('calculator.applicantAnnualIncome')}
            isMoney
            disableDecimals
            fullWidth
          />
        </Grid>

        {canHaveCoApplicant && (
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <ControlledSwitch name="hasCoApplicant" control={control} label={t('calculator.hasCoApplicant')} />
          </Grid>
        )}

        {canHaveCoApplicant && hasCoApplicant && (
          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledNumberField
              name="coApplicantAnnualIncome"
              control={control}
              label={t('calculator.coApplicantAnnualIncome')}
              isMoney
              disableDecimals
              fullWidth
            />
          </Grid>
        )}

        <Grid size={12}>
          <Accordion variant="outlined" sx={{ mt: 1 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">{t('calculator.syntheticParams.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('calculator.syntheticParams.description')}
              </Typography>
              <Stack component="dl" spacing={0.5} sx={{ m: 0 }}>
                <SyntheticParamRow
                  label={t('calculator.syntheticParams.applicantAge')}
                  value={t('calculator.syntheticParams.yearsValue', {
                    count: SYNTHETIC_PARAMS.applicantAgeYears,
                  })}
                />
                <SyntheticParamRow
                  label={t('calculator.syntheticParams.creditFileAge')}
                  value={t('calculator.syntheticParams.yearsValue', {
                    count: SYNTHETIC_PARAMS.creditFileAgeYears,
                  })}
                />
                <SyntheticParamRow
                  label={t('calculator.syntheticParams.defaultAnnualIncome')}
                  value={formatCurrency(SYNTHETIC_PARAMS.defaultAnnualIncome)}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </FieldReadyGateProvider>
  )
}

const SyntheticParamRow = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Stack direction="row" justifyContent="space-between" sx={{ m: 0 }}>
    <Typography component="dt" variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 500 }}>
      {value}
    </Typography>
  </Stack>
)

export default SimulatePrequalForm
